import axios from 'axios'

import store from '@/store/index.js'
const apiMode = process.env.VUE_APP_API_MODE

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

const oecd = axios.create({
  baseURL: 'https://sdmx.oecd.org',
  headers: {
    Accept: 'application/json, text/xml; charset=utf-8',
  },
})

const statics = axios.create({
  baseURL: process.env.BASE_URL + 'statics',
  headers: {
    Accept: 'application/json',
  },
})

function responseProxy(res) {
  if (res.status >= 400) {
    store.commit('apiError', res)
    return Promise.reject(res)
  } else {
    return Promise.resolve(res)
  }
}

function errorCatcher(errorType) {
  return function(err) {
    return data404Catcher(err).catch(err => {
      !axios.isCancel(err) && store.commit(errorType, err)
      return Promise.reject(err)
    })
  }
}

function data404Catcher(err) {
  if (/rest\/data\//.test(err.request.responseURL) && err.response.status === 404) {
    return Promise.resolve({
      ...err.response,
      status: 200,
      statusText: 'OK',
      data: {
        data: { dataSets: [{}], structures: [{ dimensions: { observation: [] } }] },
        meta: {},
        errors: [],
      },
    })
  }

  return Promise.reject(err)
}

api.interceptors.response.use(responseProxy, errorCatcher('apiError'))
statics.interceptors.response.use(responseProxy, errorCatcher('apiError'))

oecd.interceptors.response.use(function(res) {
  const eventType = /public\/rest\/dataflow/.test(res.config.url)
    ? 'metaRequest'
    : /public\/rest\/data/.test(res.config.url)
    ? 'dataRequest'
    : 'constraintsRequest'

  log([
    {
      clientEvent: eventType,
      type: res.config.method.toUpperCase(),
      status: res.status,
      host: res.config.baseURL,
      path: res.config.url.split('?')[0],
      parameters: res.config.url.split('?')[1],
    },
  ])

  if (res.status >= 400) {
    store.commit('dataError', res)
    return Promise.reject(res)
  } else return Promise.resolve(res)
}, errorCatcher('dataError'))

function apiEndpoint(path, query) {
  const queryEntries = Object.entries(query)
  if (apiMode === 'proxy') {
    const [, val] = queryEntries[0]
    return `${path}/${val}`
  }

  const search = queryEntries
    .map(([key, val]) => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(val)
    })
    .join('&')

  return `${path}?${search}`
}

function doGet(endpoint, { client, config } = { client: api, config: {} }) {
  return client.get(endpoint, config).then(res => res.data)
}

function formatLog(log) {
  log.dateTime = log.dateTime || new Date().toISOString()
  log.dateTime = log.dateTime
    .split('T')
    .join(' ')
    .replace(/\..+$/, '')

  log.sessionId = window.__sessionId
  log.versionId = window.__versionId
  log.cubeId = store.state.query.mainCubeId
  log.metaId = store.state.query.mainIndId
  log.indicatorId = store.state.tags.metaMap[log.metaId]

  return log
}

function dimensionsToSDMX(dimensions) {
  return dimensions
    .map(d => (!d.length ? [''] : d))
    .map(d => d.join('+'))
    .join('.')
}

export function getCubeMeta({ cubeId, cubeSource = 'public' }) {
  return oecd
    .get(
      `/${cubeSource.toLowerCase()}/rest/dataflow/${cubeId}/latest?references=all&detail=referencepartial`,
      {
        headers: { Accept: 'application/vnd.sdmx.structure+json; version=1.0; charset=utf-8' },
      }
    )
    .then(res => res.data.data)
}

export function getDataConstraints({
  cubeId,
  cubeSource = 'public',
  cubeVersion,
  dimensions,
  fromYear,
}) {
  const encodedId = cubeId.replace(/\//g, ',')
  const controller = new AbortController()
  const signal = controller.signal
  return [
    oecd
      .get(
        `/${cubeSource.toLowerCase()}/rest/availableconstraint/${encodedId}/${dimensionsToSDMX(
          dimensions
        )}/${cubeVersion}?startPeriod=${fromYear}&dimensionAtObservation=AllDimensions&mode=available`,
        {
          signal,
          headers: { Accept: 'application/vnd.sdmx.structure+json;version=1.0;urn=true' },
        }
      )
      .then(res => res.data),
    () => controller.abort(),
  ]
}

export function getData({ cubeId, cubeVersion, cubeSource = 'public', dimensions }) {
  const encodedId = cubeId.replace(/\//g, ',')
  const controller = new AbortController()
  const signal = controller.signal
  return [
    oecd
      .get(
        `/${cubeSource.toLowerCase()}/rest/data/${encodedId}/${dimensionsToSDMX(
          dimensions
        )}/${cubeVersion}?dimensionAtObservation=AllDimensions`,
        {
          signal,
          headers: {
            Accept: 'application/vnd.sdmx.data+json; charset=utf-8; version=2',
          },
        }
      )
      .then(response => response.data),
    () => controller.abort(),
  ]
}

export function getVersions() {
  return doGet('http://goingdigital-data.oecd.org/data-kitchen/versions/', { client: axios })
}

export function getVersionInfo({ versionId }) {
  const endpoint = apiEndpoint('external/versionInfo', { versionId })
  return doGet(endpoint)
}

export function getLanguage({ lngId }) {
  return doGet(`lng/${lngId}.json`, { client: statics })
}

export function getTagTree({ versionId }) {
  const endpoint = apiEndpoint('ind/tagTree', { versionId })
  return doGet(endpoint)
}

// export function getDataMatching(cubes) {
//   const endpoint = apiEndpoint('ind/dataMatchingPick', { cubes })
//   return doGet(endpoint)
// }

export function searchIndicators({ versionId, query }) {
  const endpoint = apiEndpoint('ind/searchIndicators', { versionId, query })
  return doGet(endpoint)
}

export function log(logs) {
  api.post(
    `log`,
    logs.map(d => formatLog(d))
  )
}

export function getDisclaimer() {
  return doGet('external/annotations')
}

export function getCountriesGroups() {
  return doGet('config/groups.json', { client: statics })
}

export function getFrontPage(versionId) {
  const endpoint = apiEndpoint('external/frontPage', { versionId })
  return doGet(endpoint)
}

export function getPopularityRanking(versionId) {
  const endpoint = apiEndpoint('ind/popularityRanking', { versionId })
  return doGet(endpoint)
}

export function getPNG(exportURL) {
  const endpoint = apiEndpoint('external/export', { exportURL, type: 'PNG' })
  return fetch(process.env.VUE_APP_API_URL + '/' + endpoint, {
    headers: {
      Accept: 'image/png',
    },
  }).then(res => {
    if (!res.ok) throw new Error()
    return res.blob()
  })
}

export function getExportSources() {
  return doGet('external/exportSources.json', { client: statics })
}
